import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import PermissionDeniedPage from '../../../pages/PermissionDeniedPage';
import type { NebulaDaysFilter } from '../../../types/nebula-insights';
import OverviewTab from './overview/OverviewTab';
import ConversationsTab from './conversations/ConversationsTab';

type NebulaInsightsTab = 'overview' | 'conversations';

const DAY_OPTIONS: Array<{ label: string; value: NebulaDaysFilter }> = [
  { label: 'Latest conversations', value: 'recent' },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'All time', value: 'all' },
];

const getSystemRole = (): string | null => {
  try {
    return sessionStorage.getItem('systemRole');
  } catch {
    return null;
  }
};

const NebulaInsights: React.FC = () => {
  const [activeTab, setActiveTab] = useState<NebulaInsightsTab>('overview');
  const [days, setDays] = useState<NebulaDaysFilter>('recent');
  const [refreshToken, setRefreshToken] = useState(0);
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null);

  if (getSystemRole() !== 'system_admin') {
    return <PermissionDeniedPage />;
  }

  const handleOpenConversation = (conversationId: string) => {
    setActiveTab('conversations');
    setPendingConversationId(conversationId);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">
            Nebula Insights
          </span>
          <p className="text-sm text-gray-500 font-family-messina-sans mt-1">
            Monitor how artists interact with Nebula across the platform.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={String(days)}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all') {
                setDays('all');
              } else if (value === 'recent') {
                setDays('recent');
              } else {
                setDays(Number(value) as NebulaDaysFilter);
              }
            }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg font-family-messina-sans text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            {DAY_OPTIONS.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setRefreshToken((value) => value + 1)}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="inline-flex gap-1 p-1 rounded-lg bg-gray-100 border border-gray-200 mb-6">
        {([
          { id: 'overview', label: 'Overview' },
          { id: 'conversations', label: 'Conversations' },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-family-messina-sans transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' ? (
        <OverviewTab
          days={days}
          refreshToken={refreshToken}
          onOpenConversation={handleOpenConversation}
        />
      ) : (
        <ConversationsTab
          days={days}
          refreshToken={refreshToken}
          selectedConversationId={pendingConversationId}
          onSelectedConversationHandled={() => setPendingConversationId(null)}
          onRequestOpenConversation={() => undefined}
        />
      )}
    </div>
  );
};

export default NebulaInsights;
